import React from "react";

// Code 39 Full ASCII ("Extended") → SVG inline. Sem dependência externa.
// Mesmo encoder usado em Pages/SantoLog/_shared/printBarcode.js (impressão),
// aqui exposto como componente React pra renderizar na tela.

const CODE39 = {
  "0": "nnnwwnwnn", "1": "wnnwnnnnw", "2": "nnwwnnnnw", "3": "wnwwnnnnn",
  "4": "nnnwwnnnw", "5": "wnnwwnnnn", "6": "nnwwwnnnn", "7": "nnnwnnwnw",
  "8": "wnnwnnwnn", "9": "nnwwnnwnn",
  "A": "wnnnnwnnw", "B": "nnwnnwnnw", "C": "wnwnnwnnn", "D": "nnnnwwnnw",
  "E": "wnnnwwnnn", "F": "nnwnwwnnn", "G": "nnnnnwwnw", "H": "wnnnnwwnn",
  "I": "nnwnnwwnn", "J": "nnnnwwwnn", "K": "wnnnnnnww", "L": "nnwnnnnww",
  "M": "wnwnnnnwn", "N": "nnnnwnnww", "O": "wnnnwnnwn", "P": "nnwnwnnwn",
  "Q": "nnnnnnwww", "R": "wnnnnnwwn", "S": "nnwnnnwwn", "T": "nnnnwnwwn",
  "U": "wwnnnnnnw", "V": "nwwnnnnnw", "W": "wwwnnnnnn", "X": "nwnnwnnnw",
  "Y": "wwnnwnnnn", "Z": "nwwnwnnnn",
  "-": "nwnnnnwnw", ".": "wwnnnnwnn", " ": "nwwnnnwnn", "$": "nwnwnwnnn",
  "/": "nwnwnnnwn", "+": "nwnnnwnwn", "%": "nnnwnwnwn", "*": "nwnnwnwnn",
};

const FULL_ASCII = [
  "%U", "$A", "$B", "$C", "$D", "$E", "$F", "$G", "$H", "$I", "$J", "$K", "$L", "$M", "$N", "$O",
  "$P", "$Q", "$R", "$S", "$T", "$U", "$V", "$W", "$X", "$Y", "$Z", "%A", "%B", "%C", "%D", "%E",
  " ", "/A", "/B", "/C", "/D", "/E", "/F", "/G", "/H", "/I", "/J", "/K", "/L", "-", ".", "/O",
  "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "/Z", "%F", "%G", "%H", "%I", "%J",
  "%V", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O",
  "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "%K", "%L", "%M", "%N", "%O",
  "%W", "+A", "+B", "+C", "+D", "+E", "+F", "+G", "+H", "+I", "+J", "+K", "+L", "+M", "+N", "+O",
  "+P", "+Q", "+R", "+S", "+T", "+U", "+V", "+W", "+X", "+Y", "+Z", "%P", "%Q", "%R", "%S", "%T",
];

function encode39(value) {
  let s = "*";
  for (const ch of String(value)) {
    const code = ch.charCodeAt(0);
    if (code > 127) continue;
    s += FULL_ASCII[code];
  }
  s += "*";
  return s;
}

function buildCode39Svg(value, { narrow = 3, ratio = 2.6, height = 160, fill = false, vertical = false } = {}) {
  const wide = Math.round(narrow * ratio);
  const encoded = encode39(value);
  const rects = [];
  let x = 0; // avança ao longo do comprimento do código
  for (let i = 0; i < encoded.length; i++) {
    const pattern = CODE39[encoded[i]];
    if (!pattern) continue;
    for (let j = 0; j < 9; j++) {
      const w = pattern[j] === "w" ? wide : narrow;
      if (j % 2 === 0) {
        // vertical: barras horizontais empilhadas (código corre no eixo Y);
        // normal: barras verticais (código corre no eixo X). `height` = comprimento da barra.
        rects.push(
          vertical
            ? `<rect x="0" y="${x}" width="${height}" height="${w}"/>`
            : `<rect x="${x}" y="0" width="${w}" height="${height}"/>`
        );
      }
      x += w;
    }
    x += narrow; // espaço estreito entre caracteres
  }
  const vbW = vertical ? height : x;
  const vbH = vertical ? x : height;
  // fill=true: o SVG é guiado pela dimensão do container mantendo a proporção,
  // então nunca "estica" e não estoura o espaço.
  const sizeStyle = fill
    ? "height:100%;width:100%"
    : vertical
      ? `width:${height}px;height:auto;max-height:100%`
      : `height:${height}px;width:auto;max-width:100%`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet" fill="#000" style="${sizeStyle};display:block">${rects.join("")}</svg>`;
}

/**
 * <Code39 value="1234" height={70} showText />
 * Renderiza um código de barras CODE 39 EXTENDED.
 */
export default function Code39({ value, height = 70, showText = false, fill = false, vertical = false, className }) {
  if (value == null || value === "") return null;
  const svg = buildCode39Svg(String(value), { height, fill, vertical });

  // fill: o SVG preenche o container (que deve ter tamanho definido), centralizado.
  if (fill) {
    return (
      <span
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          lineHeight: 0,
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  return (
    <span
      className={className}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 0 }}
    >
      <span dangerouslySetInnerHTML={{ __html: svg }} />
      {showText && (
        <span
          style={{
            fontFamily: "'Space Mono', 'Roboto Mono', monospace",
            fontSize: Math.max(10, Math.round(height * 0.16)),
            letterSpacing: "0.12em",
            color: "#111",
            marginTop: 4,
            lineHeight: 1,
          }}
        >
          *{String(value)}*
        </span>
      )}
    </span>
  );
}
